/* NEONAIL V3 desktop drawer: MENU and quick links open contextual drawer pages.
   Native Beautify mobile navigation and Shopify menu structure remain unchanged. */
(() => {
  'use strict';
  const instances = new Map();
  const desktop = window.matchMedia('(min-width: 1024px)');

  function mountWithin(root = document) {
    root.querySelectorAll('[data-nne-open]').forEach(firstTrigger => {
      const id = firstTrigger.getAttribute('data-nne-open');
      if (instances.has(id)) return;
      const suffix = id.replace(/^nne-menu-/, '');
      const holder = document.getElementById(`nne-panel-mount-${suffix}`);
      if (!holder) return;

      const triggers = Array.from(document.querySelectorAll('[data-nne-open]'))
        .filter(el => el.getAttribute('data-nne-open') === id);
      const drawer = holder.querySelector('.nne-drawer');
      const backdrop = holder.querySelector('.nne-backdrop');
      const pages = Array.from(holder.querySelectorAll('[data-nne-page]'));
      const closeButton = holder.querySelector('[data-nne-close]');
      const rootPage = holder.querySelector('[data-nne-page="root"]');
      if (!drawer || !backdrop || !closeButton || !rootPage) return;

      // The drawer is moved outside the sticky header's stacking context.
      document.body.append(holder);
      let isOpen = false;
      let activePage = rootPage;
      let lastTrigger = null;
      let previousCategory = null;
      const findPage = key => pages.find(page => page.dataset.nnePage === key);
      const setExpanded = current => triggers.forEach(trigger => {
        trigger.setAttribute('aria-expanded', String(isOpen && trigger === current));
      });

      const showPage = (page, { backward = false, reset = false, focus = true } = {}) => {
        if (!page) return;
        if (reset) {
          pages.forEach(p => {
            p.dataset.nneState = p === page ? 'center' : 'right';
            p.inert = p !== page;
            p.setAttribute('aria-hidden', String(p !== page));
          });
        } else if (activePage !== page) {
          activePage.dataset.nneState = backward ? 'right' : 'left';
          activePage.inert = true;
          activePage.setAttribute('aria-hidden', 'true');
          page.dataset.nneState = 'center';
          page.inert = false;
          page.setAttribute('aria-hidden', 'false');
        }
        activePage = page;
        if (focus) {
          const first = page.querySelector('[data-nne-back], [data-nne-goto], .nne-drawer-link');
          first?.focus({ preventScroll: true });
        }
      };

      const openMenu = (target = 'root', trigger = firstTrigger) => {
        if (!desktop.matches) return;
        const page = findPage(target) || rootPage;
        lastTrigger = trigger;
        if (isOpen) {
          showPage(page);
          setExpanded(trigger);
          return;
        }
        isOpen = true;
        showPage(page, { reset: true, focus: false });
        drawer.inert = false;
        drawer.setAttribute('aria-hidden', 'false');
        drawer.classList.add('is-open');
        backdrop.classList.add('is-open');
        document.body.classList.add('nne-drawer-is-open');
        setExpanded(trigger);
        closeButton.focus({ preventScroll: true });
      };

      const closeMenu = () => {
        if (!isOpen) return;
        isOpen = false;
        drawer.classList.remove('is-open');
        backdrop.classList.remove('is-open');
        document.body.classList.remove('nne-drawer-is-open');
        drawer.setAttribute('aria-hidden', 'true');
        setExpanded(null);
        if (lastTrigger?.isConnected) lastTrigger.focus({ preventScroll: true });
        drawer.inert = true;
      };

      const onTriggers = triggers.map(trigger => {
        const handler = event => {
          if (!desktop.matches) return; // Ordinary links work normally on mobile.
          const target = trigger.dataset.nnePanelTarget || 'root';
          if (!findPage(target)) return; // Preserve normal navigation if missing.
          event.preventDefault();
          // Clicking MENU again closes the complete list.
          if (isOpen && trigger === lastTrigger && target === 'root') closeMenu();
          else openMenu(target, trigger);
        };
        trigger.addEventListener('click', handler);
        return { trigger, handler };
      });

      const onBackdrop = () => closeMenu();
      const onClose = () => closeMenu();
      const onMenuClick = evt => {
        const next = evt.target.closest('[data-nne-goto]');
        if (next) {
          previousCategory = next;
          showPage(findPage(next.dataset.nneGoto));
          return;
        }
        const back = evt.target.closest('[data-nne-back]');
        if (back) {
          showPage(findPage(back.dataset.nneBack), { backward: true });
          if (previousCategory?.isConnected && previousCategory.closest('[data-nne-state="center"]')) {
            previousCategory.focus({ preventScroll: true });
          }
        }
      };
      const onKeyDown = event => {
        if (!isOpen) return;
        if (event.key === 'Escape') { event.preventDefault(); closeMenu(); return; }
        if (event.key !== 'Tab') return;
        const focusable = Array.from(drawer.querySelectorAll('a[href],button:not([disabled])'))
          .filter(el => !el.closest('[inert]') && el.getClientRects().length);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      };
      const onMedia = () => { if (!desktop.matches) closeMenu(); };
      backdrop.addEventListener('click', onBackdrop);
      closeButton.addEventListener('click', onClose);
      drawer.addEventListener('click', onMenuClick);
      document.addEventListener('keydown', onKeyDown);
      desktop.addEventListener('change', onMedia);

      instances.set(id, { destroy() {
        closeMenu();
        onTriggers.forEach(({ trigger, handler }) => trigger.removeEventListener('click', handler));
        backdrop.removeEventListener('click', onBackdrop);
        closeButton.removeEventListener('click', onClose);
        drawer.removeEventListener('click', onMenuClick);
        document.removeEventListener('keydown', onKeyDown);
        desktop.removeEventListener('change', onMedia);
        holder.remove();
      } });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountWithin());
  else mountWithin();
  document.addEventListener('shopify:section:load', event => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const trigger = event.target.querySelector('[data-nne-open]');
    if (!trigger) return;
    const id = trigger.getAttribute('data-nne-open');
    instances.get(id)?.destroy();
    instances.delete(id);
  });
})();

